/**
 * BookingForm - Form component for collecting booker information.
 * Used in the public booking flow after selecting a time slot.
 */

import { ChevronLeft, Mail01, Plus, User01, X } from '@untitledui/icons';
import {
  type ChangeEvent,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  AvailableSlot,
  CustomAnswers,
  CustomAnswerValue,
  CustomQuestion,
  PublicBookingType,
} from '@/lib/api/types/scheduling';
import { Button } from '@/lib/base/buttons/button';
import type { FieldConfig } from '@/lib/base/forms';
import {
  FormField,
  FormProvider,
  InputBase,
  InputGroup,
  SubmitButton,
  useFormContext,
} from '@/lib/base/forms';
import { Label } from '@/lib/base/forms/input/label';
import { isApiError } from '@/lib/errors';
import { logger } from '@/lib/logger';
import { sanitizeError } from '@/lib/utils/errors';
import { cx } from '@/utils/cx';

import { validateBookingEmail } from './bookingEmailValidation';
import { CustomQuestionField } from './CustomQuestionField';
import {
  buildCustomAnswersPayload,
  customFieldKey,
  isAnswerEmpty,
  parseCustomAnswerErrors,
  seedAnswers,
} from './customQuestions';

// ============================================================================
// Types
// ============================================================================

export interface BookingFormData {
  email: string;
  name: string;
  notes: string;
  additionalGuests: string[];
  customAnswers?: CustomAnswers;
}

export interface BookingFormProps {
  /** The selected time slot */
  selectedSlot: AvailableSlot;
  /** The booking type details */
  bookingType: PublicBookingType;
  /** Callback when form is submitted */
  onSubmit: (data: BookingFormData) => Promise<void>;
  /** Callback when user clicks back */
  onBack: () => void;
  /** Timezone for display. REQUIRED — the caller resolves it (booker's picker
   * selection on public pages; profile timezone on authenticated previews). */
  timezone: string;
  /** Prefill for the email field (e.g. from a ?email= link parameter) */
  initialEmail?: string;
  /** Prefill for the name field (e.g. from a ?name= link parameter) */
  initialName?: string;
  /** Org-configured custom questions, rendered after the Name/Email fields and above Notes. */
  customQuestions?: CustomQuestion[];
}

// ============================================================================
// Constants
// ============================================================================

const MAX_ADDITIONAL_GUESTS = 10;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formats a time slot for display.
 */
/**
 * Best-effort macOS detection for the cmd/ctrl key hint. Uses navigator.platform
 * with userAgent fallback; deliberately conservative — non-mac users see "Ctrl",
 * which is correct on Windows/Linux and harmless to mac users who would also
 * recognise that ctrl+enter still works.
 */
function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const platform = navigator.platform || '';
  if (platform) {
    return /Mac|iPhone|iPad|iPod/.test(platform);
  }
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent || '');
}

// `formatSlotDisplay` used to feed the embedded summary block here. That
// block is now part of BookingHostCard (see BookingPage), so the helper
// has no callers left in this file. Removed.

/** First-wins dedupe by `key` — a host misconfig producing duplicate keys
 *  must not register duplicate form fields. */
function dedupeQuestionsByKey(questions: CustomQuestion[]): CustomQuestion[] {
  const seen = new Set<string>();
  const deduped: CustomQuestion[] = [];
  for (const question of questions) {
    if (!seen.has(question.key)) {
      seen.add(question.key);
      deduped.push(question);
    }
  }
  return deduped;
}

// ============================================================================
// Guest Row Sub-component
// ============================================================================

interface GuestRowProps {
  guestKey: string;
  guestIndex: number;
  guest: string;
  onGuestChange: (index: number, value: string) => void;
  onRemoveGuest: (index: number) => void;
}

function GuestRow({
  guestKey,
  guestIndex,
  guest,
  onGuestChange,
  onRemoveGuest,
}: GuestRowProps) {
  const handleChange = useCallback(
    (v: string) => onGuestChange(guestIndex, v),
    [guestIndex, onGuestChange]
  );
  const handleRemove = useCallback(
    () => onRemoveGuest(guestIndex),
    [guestIndex, onRemoveGuest]
  );
  return (
    <div className='flex items-center gap-1.5'>
      <div className='flex-1 min-w-0'>
        <FormField name={guestKey}>
          {({ isInvalid, showError, inputId }) => (
            <InputGroup
              value={guest}
              onChange={handleChange}
              isInvalid={isInvalid}
              showError={showError}
            >
              <InputBase
                id={inputId}
                type='email'
                placeholder='guest@example.com'
                icon={Mail01}
                aria-label={`Guest ${guestIndex + 1} email address`}
                autoComplete='email'
              />
            </InputGroup>
          )}
        </FormField>
      </div>
      {/* Quiet remove affordance — ghost icon button so it reads as a "×"
          on the row, not a separate action with weight equal to the input.
          Cal.com keeps remove this subtle on guest chips. */}
      <button
        type='button'
        onClick={handleRemove}
        aria-label={`Remove guest ${guestIndex + 1}`}
        className='inline-flex size-9 shrink-0 items-center justify-center rounded-md text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-error-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-600)]'
      >
        <X className='size-4' />
      </button>
    </div>
  );
}

// ============================================================================
// Inner Component
// ============================================================================

interface BookingFormContentProps {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  name: string;
  setName: Dispatch<SetStateAction<string>>;
  notes: string;
  setNotes: Dispatch<SetStateAction<string>>;
  additionalGuests: string[];
  setAdditionalGuests: Dispatch<SetStateAction<string[]>>;
  guestIds: MutableRefObject<number[]>;
  nextGuestId: MutableRefObject<number>;
  isSubmitting: boolean;
  setIsSubmitting: Dispatch<SetStateAction<boolean>>;
  onSubmit: (data: BookingFormData) => Promise<void>;
  onBack: () => void;
  selectedSlot: AvailableSlot;
  bookingType: PublicBookingType;
  timezone: string;
  questions: CustomQuestion[];
  answers: Record<string, CustomAnswerValue>;
  setAnswers: Dispatch<SetStateAction<Record<string, CustomAnswerValue>>>;
  serverErrorKeys: string[];
  setServerErrorKeys: Dispatch<SetStateAction<string[]>>;
}

function BookingFormContent({
  email,
  setEmail,
  name,
  setName,
  notes,
  setNotes,
  additionalGuests,
  setAdditionalGuests,
  guestIds,
  nextGuestId,
  isSubmitting,
  setIsSubmitting,
  onSubmit,
  onBack,
  // `selectedSlot`, `bookingType`, and `timezone` were consumed by the
  // embedded summary block that has since moved to BookingHostCard.
  // Kept in the prop signature (prefixed with _) so the outer BookingForm
  // contract doesn't have to change in lockstep — easier rollback if the
  // host-card-driven summary needs to be reverted.
  selectedSlot: _selectedSlot,
  bookingType: _bookingType,
  timezone: _timezone,
  questions,
  answers,
  setAnswers,
  // Only the setter is needed here — the catch handler writes to it and
  // `handleAnswerChange` clears entries as the booker edits. Reading the
  // current keys happens in the outer BookingForm's fieldConfig validator.
  serverErrorKeys: _serverErrorKeys,
  setServerErrorKeys,
}: BookingFormContentProps) {
  const {
    resetField,
    setSubmitError,
    submitError,
    touchAll,
    isValid,
    errorSummary,
    scrollToField,
  } = useFormContext();

  const formId = useId();
  const notesId = `${formId}-notes`;

  // slotDisplay and locationDisplay used to feed the embedded summary block
  // (Calendar / Clock / VideoRecorder rows). That block now lives in the
  // parent BookingHostCard, so we no longer compute those values here.

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const validGuests = additionalGuests
        .map(g => g.trim())
        .filter(g => g.length > 0);

      await onSubmit({
        email: email.trim(),
        name: name.trim(),
        notes: notes.trim(),
        additionalGuests: validGuests,
        ...(questions.length > 0
          ? { customAnswers: buildCustomAnswersPayload(questions, answers) }
          : {}),
      });
      setServerErrorKeys([]);
    } catch (err) {
      if (isApiError(err) && err.statusCode === 400) {
        const keys = parseCustomAnswerErrors(err.response, questions);
        if (keys.length > 0) {
          setServerErrorKeys(keys);
          touchAll();
          return;
        }
      }
      logger.error(
        'Error booking meeting',
        err instanceof Error ? err : undefined,
        { component: 'BookingForm' }
      );
      setSubmitError(
        sanitizeError(
          err instanceof Error
            ? err.message
            : "We couldn't book this meeting. Try again."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    email,
    name,
    notes,
    additionalGuests,
    questions,
    answers,
    onSubmit,
    setIsSubmitting,
    setSubmitError,
    setServerErrorKeys,
    touchAll,
  ]);

  const handleAnswerChange = useCallback(
    (key: string, value: CustomAnswerValue) => {
      setAnswers(prev => ({ ...prev, [key]: value }));
      setServerErrorKeys(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : prev
      );
    },
    [setAnswers, setServerErrorKeys]
  );

  const handleAddGuest = useCallback(() => {
    if (additionalGuests.length >= MAX_ADDITIONAL_GUESTS) {
      return;
    }
    guestIds.current = [...guestIds.current, nextGuestId.current++];
    setAdditionalGuests(prev => [...prev, '']);
  }, [additionalGuests.length, guestIds, nextGuestId, setAdditionalGuests]);

  const handleRemoveGuest = useCallback(
    (index: number) => {
      const guestKey = `guest:${guestIds.current[index]}:email`;
      resetField(guestKey);

      const newIds = [...guestIds.current];
      newIds.splice(index, 1);
      guestIds.current = newIds;

      setAdditionalGuests(prev => {
        const newGuests = [...prev];
        newGuests.splice(index, 1);
        return newGuests;
      });
    },
    [guestIds, resetField, setAdditionalGuests]
  );

  const handleGuestChange = useCallback(
    (index: number, value: string) => {
      setAdditionalGuests(prev => {
        const newGuests = [...prev];
        newGuests[index] = value;
        return newGuests;
      });
    },
    [setAdditionalGuests]
  );

  const handleEmailChange = useCallback((v: string) => setEmail(v), [setEmail]);

  const handleNameChange = useCallback((v: string) => setName(v), [setName]);

  const handleNotesChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value),
    [setNotes]
  );

  // Cmd/Ctrl+Enter submits the booking from anywhere inside the form. Most
  // power users expect this shortcut on data-entry surfaces (Slack, Linear,
  // Notion, Gmail compose). The form's `noValidate` setting means we can't
  // rely on native form submission for this; bind on the form element so
  // the listener only fires while focus is inside the booking form, not
  // when the user is on the calendar / success steps.
  //
  // IMPORTANT: route through the same validation gate as `SubmitButton`
  // (touchAll → check isValid → either submit or scroll to the first
  // errored field). A naked `handleSubmit()` here would bypass the
  // FormProvider's validation and allow the keyboard shortcut to send
  // invalid data to the server even when the disabled-state submit button
  // would have rejected the click. The only carve-out matches SubmitButton:
  // when `submitError` is the only thing blocking (no field errors), we
  // clear it and retry so a transient server failure isn't a dead end.
  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') {
        return;
      }
      e.preventDefault();
      if (isSubmitting) {
        return;
      }

      // Mirror SubmitButton.handleClick: clear stale submit errors first.
      const hasFieldErrors = errorSummary.length > 0;
      const onlySubmitError = !!submitError && !hasFieldErrors;
      if (submitError) {
        setSubmitError(null);
      }
      // Reveal any blur-deferred errors so the booker can see what's wrong.
      touchAll();

      if (isValid || onlySubmitError) {
        void handleSubmit();
      } else if (hasFieldErrors) {
        // Match the click flow: scroll to the first errored field after
        // `touchAll` triggers a re-render that surfaces the inline errors.
        requestAnimationFrame(() => {
          scrollToField(errorSummary[0].name);
        });
      }
    },
    [
      handleSubmit,
      isSubmitting,
      isValid,
      errorSummary,
      submitError,
      setSubmitError,
      touchAll,
      scrollToField,
    ]
  );

  return (
    <div>
      {/* Booking form — host context (title, selected date/time, location)
          lives in the parent BookingHostCard so this component focuses on
          the data-entry task only. */}
      <form noValidate className='space-y-5' onKeyDown={handleFormKeyDown}>
        {/* Name field (placed first — natural reading order, matches Cal.com
            / Calendly conventions where identity comes before contact). */}
        <FormField name='name' label='Your name' isRequired>
          {({ isInvalid, showError, inputId }) => (
            <InputGroup
              value={name}
              onChange={handleNameChange}
              isInvalid={isInvalid}
              showError={showError}
            >
              <InputBase
                id={inputId}
                type='text'
                placeholder='Your full name'
                icon={User01}
                autoComplete='name'
              />
            </InputGroup>
          )}
        </FormField>

        {/* Email field */}
        <FormField name='email' label='Email address' isRequired>
          {({ isInvalid, showError, inputId }) => (
            <InputGroup
              value={email}
              onChange={handleEmailChange}
              isInvalid={isInvalid}
              showError={showError}
            >
              <InputBase
                id={inputId}
                type='email'
                placeholder='you@example.com'
                icon={Mail01}
                autoComplete='email'
              />
            </InputGroup>
          )}
        </FormField>

        {/* Org-configured custom questions — rendered directly after Name/Email,
            above Notes and the quiet "Add guests" affordance, per user request. */}
        {questions.map(q => (
          <CustomQuestionField
            key={q.key}
            question={q}
            value={answers[q.key]}
            onChange={handleAnswerChange}
          />
        ))}

        {/* Notes field (optional) */}
        <div className='space-y-1.5'>
          <Label htmlFor={notesId}>Notes (optional)</Label>
          <textarea
            id={notesId}
            placeholder="Anything you'd like us to know before the meeting"
            value={notes}
            onChange={handleNotesChange}
            rows={3}
            className={cx(
              'w-full rounded-lg bg-primary px-3 py-2 text-md text-primary shadow-xs ring-1 ring-primary ring-inset',
              'placeholder:text-placeholder',
              'focus:ring-2 focus:ring-brand focus:outline-none',
              'transition-shadow duration-100 ease-linear'
            )}
          />
        </div>

        {/* Guests section — progressively disclosed.
            Empty state: a single quiet "Add guests" link, no label / counter.
            With ≥1 guest: small caption + list + "Add another" link.
            Cal.com / Calendly both treat this as opt-in chrome; surfacing a
            bordered "Additional Guests (optional)" block when there are no
            guests adds visual weight equal to the required fields and
            misleads the booker about importance. */}
        {additionalGuests.length === 0 ? (
          <button
            type='button'
            onClick={handleAddGuest}
            className='inline-flex items-center gap-1.5 text-sm font-medium text-fg-brand-primary transition-colors hover:text-utility-brand-700 hover:underline focus:outline-none focus-visible:underline'
          >
            <Plus className='size-4' />
            Add guests
          </button>
        ) : (
          <div className='space-y-2'>
            <div className='flex items-baseline justify-between'>
              <span className='text-sm font-medium text-[var(--color-text-primary)]'>
                Guests
              </span>
              {/* Surface the counter only as the booker approaches the cap.
                  Below 80% it's noise; near the cap it's useful. */}
              {additionalGuests.length >=
                Math.floor(MAX_ADDITIONAL_GUESTS * 0.8) && (
                <span className='text-xs text-[var(--color-text-tertiary)]'>
                  {additionalGuests.length} of {MAX_ADDITIONAL_GUESTS}
                </span>
              )}
            </div>

            {additionalGuests
              .map((guest, i) => ({
                guest,
                stableKey: guestIds.current[i] ?? i,
                guestIndex: i,
              }))
              .map(({ guest, stableKey, guestIndex }) => {
                const guestId = guestIds.current[guestIndex];
                const guestKey = `guest:${guestId}:email`;
                return (
                  <GuestRow
                    key={stableKey}
                    guestKey={guestKey}
                    guestIndex={guestIndex}
                    guest={guest}
                    onGuestChange={handleGuestChange}
                    onRemoveGuest={handleRemoveGuest}
                  />
                );
              })}

            {additionalGuests.length < MAX_ADDITIONAL_GUESTS && (
              <button
                type='button'
                onClick={handleAddGuest}
                className='inline-flex items-center gap-1.5 pt-1 text-sm font-medium text-fg-brand-primary transition-colors hover:text-utility-brand-700 hover:underline focus:outline-none focus-visible:underline'
              >
                <Plus className='size-4' />
                Add another
              </button>
            )}
          </div>
        )}

        {/* Error message */}
        {submitError && (
          <div
            role='alert'
            className='rounded-lg bg-error-50 p-4 text-sm text-error-primary'
          >
            {submitError}
          </div>
        )}

        {/* Footer — Back / Confirm row anchored at the bottom of the form,
            matching Cal.com's right-aligned action pattern. Back stays a
            quiet button so Confirm reads as the primary action. */}
        <div className='flex flex-wrap items-center justify-between gap-3 pt-4'>
          {/* Discoverability hint for the cmd/ctrl+Enter shortcut. Detect the
              platform so we don't show ⌘ to Windows / Linux users. */}
          <span
            className='hidden text-xs text-[var(--color-text-tertiary)] sm:inline'
            aria-hidden='true'
          >
            Press{' '}
            <kbd className='inline-flex items-center rounded border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 font-mono text-[10px] font-medium text-[var(--color-text-secondary)]'>
              {isMacPlatform() ? '⌘' : 'Ctrl'}
            </kbd>{' '}
            +{' '}
            <kbd className='inline-flex items-center rounded border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 font-mono text-[10px] font-medium text-[var(--color-text-secondary)]'>
              Enter
            </kbd>{' '}
            to confirm
          </span>
          <div className='flex items-center gap-3 sm:ml-auto'>
            <Button
              type='button'
              color='tertiary'
              size='lg'
              onClick={onBack}
              iconLeading={ChevronLeft}
              isDisabled={isSubmitting}
            >
              Back
            </Button>
            <SubmitButton
              color='primary'
              size='lg'
              onClick={handleSubmit}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Confirm booking
            </SubmitButton>
          </div>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// Outer Component
// ============================================================================

export function BookingForm({
  selectedSlot,
  bookingType,
  onSubmit,
  onBack,
  timezone,
  initialEmail = '',
  initialName = '',
  customQuestions,
}: BookingFormProps) {
  // Fall back inside the memo (not via a default param) — a default `[]`
  // param creates a new array reference on every render, which would churn
  // `formValues`'s identity and re-trigger FormProvider's submitError
  // auto-clear effect on every unrelated re-render (e.g. isSubmitting
  // toggling). Keeping the raw `customQuestions` prop (stable `undefined`
  // when omitted) as the dep avoids that.
  const questions = useMemo(
    () => dedupeQuestionsByKey(customQuestions ?? []),
    [customQuestions]
  );

  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState(initialName);
  const [notes, setNotes] = useState('');
  const [additionalGuests, setAdditionalGuests] = useState<string[]>([]);

  // Stable key generation for guest rows (avoids index-based keys on mutable list)
  const nextGuestId = useRef(0);
  const guestIds = useRef<number[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Seeded once from the booking type's questions — a loaded booking type's
  // question set is fixed for the component's life, so this deliberately
  // does not re-seed on `questions` identity changes (that would wipe
  // in-progress answers and can loop if the parent recreates the array).
  const [answers, setAnswers] = useState<Record<string, CustomAnswerValue>>(
    () => seedAnswers(questions)
  );
  const [serverErrorKeys, setServerErrorKeys] = useState<string[]>([]);

  const fieldConfig = useMemo<Record<string, FieldConfig>>(() => {
    const serverErrorKeySet = new Set(serverErrorKeys);
    const config: Record<string, FieldConfig> = {
      email: {
        required: true,
        validate: v => validateBookingEmail(v),
      },
      name: { required: true },
    };
    additionalGuests.forEach((_, i) => {
      const guestId = guestIds.current[i];
      config[`guest:${guestId}:email`] = {
        validate: v => validateBookingEmail(v),
      };
    });
    questions.forEach(q => {
      const key = customFieldKey(q.key);
      config[key] = {
        validate: v => {
          if (serverErrorKeySet.has(q.key)) {
            return "This answer wasn't accepted – please update it and try again.";
          }
          const hasOptions =
            q.type !== 'single_select' && q.type !== 'multi_select'
              ? true
              : (q.options ?? []).length > 0;
          if (
            q.required &&
            hasOptions &&
            isAnswerEmpty(v as CustomAnswerValue, q.type)
          ) {
            return 'Required';
          }
          return undefined;
        },
      };
    });
    return config;
  }, [additionalGuests, questions, serverErrorKeys]);

  const formValues = useMemo(() => {
    const values: Record<string, string | string[] | boolean> = {
      email,
      name,
    };
    additionalGuests.forEach((guest, i) => {
      const guestId = guestIds.current[i];
      values[`guest:${guestId}:email`] = guest;
    });
    questions.forEach(q => {
      values[customFieldKey(q.key)] = answers[q.key];
    });
    return values;
  }, [email, name, additionalGuests, questions, answers]);

  const fieldLabels = useMemo<Record<string, string>>(() => {
    const labels: Record<string, string> = { email: 'Email', name: 'Name' };
    additionalGuests.forEach((_, i) => {
      const guestId = guestIds.current[i];
      labels[`guest:${guestId}:email`] = `Guest ${i + 1} email`;
    });
    questions.forEach(q => {
      labels[customFieldKey(q.key)] = q.label;
    });
    return labels;
  }, [additionalGuests, questions]);

  return (
    <FormProvider
      fields={fieldConfig}
      values={formValues}
      fieldLabels={fieldLabels}
    >
      <BookingFormContent
        email={email}
        setEmail={setEmail}
        name={name}
        setName={setName}
        notes={notes}
        setNotes={setNotes}
        additionalGuests={additionalGuests}
        setAdditionalGuests={setAdditionalGuests}
        guestIds={guestIds}
        nextGuestId={nextGuestId}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        onSubmit={onSubmit}
        onBack={onBack}
        selectedSlot={selectedSlot}
        bookingType={bookingType}
        timezone={timezone}
        questions={questions}
        answers={answers}
        setAnswers={setAnswers}
        serverErrorKeys={serverErrorKeys}
        setServerErrorKeys={setServerErrorKeys}
      />
    </FormProvider>
  );
}
